import React, { useState } from 'react';
import { Building, DollarSign, Calculator, FileText, AlertTriangle } from 'lucide-react';
import { Card, Button } from '../ui';

export function HoaSpecialAssessmentCalculator() {
  const [totalCost, setTotalCost] = useState<number>(120000);
  const [numUnits, setNumUnits] = useState<number>(40);
  const [months, setMonths] = useState<number>(24);
  const [interestRate, setInterestRate] = useState<number>(0);

  // Math calculations
  const totalPerUnit = totalCost / (numUnits || 1);
  
  // If there's an interest rate, calculate loan amortization per unit
  let monthlyPerUnit = 0;
  let totalInterestPerUnit = 0;

  if (interestRate > 0 && months > 0) {
    const monthlyRate = (interestRate / 100) / 12;
    monthlyPerUnit = totalPerUnit * (monthlyRate * Math.pow(1 + monthlyRate, months)) / (Math.pow(1 + monthlyRate, months) - 1);
    totalInterestPerUnit = (monthlyPerUnit * months) - totalPerUnit;
  } else if (months > 0) {
    monthlyPerUnit = totalPerUnit / months;
  }

  const grandTotalPerUnit = totalPerUnit + totalInterestPerUnit;
  const grandTotalProject = totalCost + (totalInterestPerUnit * numUnits);

  return (
    <div className="max-w-5xl mx-auto p-4 space-y-8">
      {/* Header */}
      <div className="bg-slate-900 rounded-3xl p-8 md:p-12 text-white shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-center gap-8 border-t-8 border-teal-500">
        <div className="absolute top-0 right-0 p-8 opacity-10 pointer-events-none">
          <Building className="w-64 h-64" />
        </div>
        <div className="flex-1 z-10">
          <div className="flex items-center gap-2 text-teal-400 font-bold tracking-widest uppercase mb-4 text-sm">
            <AlertTriangle className="w-4 h-4" /> HOA & Condo Boards
          </div>
          <h2 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">
            Special Assessment Calculator
          </h2>
          <p className="text-slate-300 max-w-2xl text-lg font-medium">
            Take the emotion out of unexpected community repairs. Instantly generate a clear, transparent breakdown of the exact cost per homeowner.
          </p>
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-8">
        
        {/* Input Column */}
        <div className="md:col-span-4 space-y-6">
          <Card className="p-6 bg-white dark:bg-slate-900 shadow-xl">
            <h3 className="font-bold text-xl mb-6 flex items-center text-slate-800 dark:text-white">
              <Calculator className="w-5 h-5 mr-2 text-teal-600" /> Project Details
            </h3>
            
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Total Project Cost ($)</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="number"
                    value={totalCost}
                    onChange={(e) => setTotalCost(Number(e.target.value))}
                    className="w-full h-12 pl-10 pr-4 rounded-xl border-2 border-slate-200 focus:border-teal-500 outline-none font-bold text-lg bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Number of Homes/Units</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Building className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="number"
                    value={numUnits}
                    onChange={(e) => setNumUnits(Number(e.target.value))}
                    className="w-full h-12 pl-10 pr-4 rounded-xl border-2 border-slate-200 focus:border-teal-500 outline-none font-bold text-lg bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Payment Timeline (Months)</label>
                <input
                  type="number"
                  value={months}
                  onChange={(e) => setMonths(Number(e.target.value))}
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-teal-500 outline-none font-bold text-lg bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">HOA Loan Interest Rate (%) <span className="text-slate-400 font-normal">(Optional)</span></label>
                <input
                  type="number"
                  value={interestRate}
                  onChange={(e) => setInterestRate(Number(e.target.value))}
                  step="0.1"
                  className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 focus:border-teal-500 outline-none font-bold text-lg bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-white"
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Results Column */}
        <div className="md:col-span-8">
          <Card className="h-full p-8 bg-slate-50 dark:bg-slate-900 border-2 shadow-inner">
            <div className="flex justify-between items-start mb-8 border-b pb-6 dark:border-slate-800">
              <div>
                <h3 className="text-2xl font-black text-slate-800 dark:text-white">Assessment Impact Report</h3>
                <p className="text-slate-500 mt-1">Cost breakdown per individual household.</p>
              </div>
              <Button variant="secondary" onClick={() => window.print()} className="border-teal-600 text-teal-700 hover:bg-teal-50 hidden md:flex">
                <FileText className="w-4 h-4 mr-2" /> Print PDF
              </Button>
            </div>

            {/* The Big Number */}
            <div className="bg-white dark:bg-slate-950 p-8 rounded-2xl border-2 border-teal-100 dark:border-slate-800 shadow-sm mb-8 text-center">
              <div className="text-slate-500 font-bold uppercase tracking-widest text-sm mb-2">Monthly Addition to Dues</div>
              <div className="text-6xl font-black text-teal-600 dark:text-teal-400">
                ${monthlyPerUnit.toFixed(2)}
              </div>
              <div className="text-slate-400 mt-2 font-medium">Per home, for {months} months</div>
            </div>

            {/* Breakdown Grid */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-slate-950 p-6 rounded-xl border dark:border-slate-800">
                <div className="text-slate-500 text-sm font-bold mb-1">Base Cost Per Home</div>
                <div className="text-2xl font-black text-slate-800 dark:text-white">${totalPerUnit.toFixed(2)}</div>
              </div>
              
              <div className="bg-white dark:bg-slate-950 p-6 rounded-xl border dark:border-slate-800">
                <div className="text-slate-500 text-sm font-bold mb-1">Interest Per Home</div>
                <div className="text-2xl font-black text-slate-800 dark:text-white">${totalInterestPerUnit.toFixed(2)}</div>
              </div>

              <div className="bg-slate-100 dark:bg-slate-800 p-6 rounded-xl border dark:border-slate-700 sm:col-span-2 flex justify-between items-center">
                <div>
                  <div className="text-slate-600 dark:text-slate-300 text-sm font-bold mb-1">Total Liability Per Home</div>
                  <div className="text-xs text-slate-400">Principal + Interest</div>
                </div>
                <div className="text-3xl font-black text-slate-800 dark:text-white">
                  ${grandTotalPerUnit.toFixed(2)}
                </div>
              </div>
            </div>

            {/* Community Totals */}
            <div className="mt-8 pt-8 border-t dark:border-slate-800">
              <h4 className="font-bold text-slate-800 dark:text-white mb-4">Community Grand Totals</h4>
              <div className="flex justify-between items-center text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                <span>Total Project Base Cost:</span>
                <span className="font-bold text-slate-800 dark:text-white">${totalCost.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="flex justify-between items-center text-sm font-medium text-slate-600 dark:text-slate-400 mb-2">
                <span>Total Community Interest ({interestRate}%):</span>
                <span className="font-bold text-slate-800 dark:text-white">${(totalInterestPerUnit * numUnits).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
              <div className="flex justify-between items-center text-lg font-bold text-slate-800 dark:text-white mt-4 pt-4 border-t border-dashed dark:border-slate-700">
                <span>Final Community Cost:</span>
                <span className="text-teal-600 dark:text-teal-400">${grandTotalProject.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
              </div>
            </div>

          </Card>
        </div>

      </div>
    </div>
  );
}
